import {AxiosHeaders} from 'axios';
import {ObjectUtils} from '../../Utils/ObjectUtils';
import autobind from "autobind-decorator";
import {config} from '../../Config';
import {SET_LOADING} from "../../Redux/Actions/Types";
import {default as DateUtils} from "../../../../monitoring/src/Utils/DateUtils";
import sbAxios from './AxiosConfig';
import {
    initializeBaseServiceState,
    getAuthToken,
    setAuthToken,
    getTokenData,
    getPermissions,
    setPermissionsValue,
    getNotifications,
    setNotificationsValue,
} from './BaseServiceState';
// import * as models from "../../Models/StreamBank";

initializeBaseServiceState();

let pendingAjax = 0;

export class BaseService {
    constructor() {}

    static get GET() { return 'GET'; }
    static get POST() { return 'POST'; }
    static get PUT() { return 'PUT'; }
    static get DELETE() { return 'DELETE'; }

    static get AUTH_TOKEN() {
        return getAuthToken();
    }
    static set AUTH_TOKEN(value) {
        setAuthToken(value);
    }
    static get TOKEN_DATA() {
        return getTokenData();
    }

    static get PERMISSIONS() {
        return getPermissions();
    }
    static set PERMISSIONS(value) {
        setPermissionsValue(value);
    }

    static get NOTIFICATIONS() {
        return getNotifications();
    }
    static set NOTIFICATIONS(value) {
        setNotificationsValue(value);
    }

    static get IS_AUTHENTICATED() {
        const tokenData = getTokenData();
        if (getAuthToken() === '') {
            return false;
        } else if (tokenData.twoFactorRequired) {
            return false;
        } else if (tokenData.publicContext) {
            return false;
        } else if (tokenData.activeUntil) {
            let activeUntil = DateUtils.ParseUTCDateTime(tokenData.activeUntil);
            let now = new Date();

            if (activeUntil < now) {
                return false;
            }
        }
        return true;
    }

    get Url() { return config.getApiUrl() + "/api"; }
    get ModelName() { console.log("Not implemented."); return "Model Name"; }
    
    get CssClassName() {
        return this.ModelName.replace(/\s+/g, '-').toLocaleLowerCase();
    }

    // get Model() { return models?.[this.ModelName?.replace(/ /g, '')]; }
    
    // can be overridden for non-standard plurals
    get ModelNamePlural() { return this?.ModelName + "s"; }

    get PrimaryKeyProperty() { console.log("Not implemented."); return null; }
    get PrimaryKeyPropertyPlural() { return this.PrimaryKeyProperty + 's'; }
    
    get TitleProperty() { return "name"; }
    get SubtitleProperty() { return null; }
    get SearchProperties() { return [this.TitleProperty, this.SubtitleProperty].filter(Boolean); }

    SetLoading(loading = true) {
        config.getStore().dispatch({type: SET_LOADING, value: loading});
    }

    SendRequest(method, postData = null, overrideJwt = null, urlSuffix = "", inBackground = false, requestOptions = {}) {
        return new Promise((resolve, reject) => {
            let url = this.Url + urlSuffix;

            switch(method) {
                case BaseService.GET:
                    if(postData) {
                        if(typeof postData === 'object') {
                            if(Object.keys(postData).length) {
                                url = url + "?" + ObjectUtils.BuildQueryString(postData);
                            }
                        } else {
                            url = url + "/" + postData;
                        }
                    }
                    break;
                case BaseService.POST:
                    break;
                case BaseService.PUT:
                    break;
                case BaseService.DELETE:
                    if(postData) {
                        url = url + "/" + postData;
                    }
                    break;
            }

            if(!inBackground) {
                pendingAjax++;
                if (pendingAjax === 1) {
                    this.SetLoading();
                }
            }

            const {headers: overriddenHeaders, ...otherRequestOptions} = requestOptions ?? {};

            const requestMethod = method?.toLowerCase?.() ?? BaseService.GET.toLowerCase();
            const headers = AxiosHeaders.concat(
                sbAxios.defaults.headers?.common ?? {},
                overriddenHeaders,
            );
            if(overrideJwt) {
                headers.set('Authorization', overrideJwt);
            }

            const requestConfig = {
                method: requestMethod,
                url,
                withCredentials: true,
                headers,
                ...otherRequestOptions,
            };

            if(method !== BaseService.GET && method !== BaseService.DELETE) {
                requestConfig.data = postData != null ? this._CleanData(postData) : postData;
            }

            sbAxios(requestConfig)
                .then((response) => {
                    resolve(response?.data ?? {});
                })
                .catch((error) => {
                    reject(error);
                })
                .finally(() => {
                    if(!inBackground) {
                        pendingAjax--;
                        if (pendingAjax === 0) {
                            this.SetLoading(false);
                        }
                    }
                });
        });
    }

    _CleanData(data) {
        //clone the data so we don't goof up anything with the ui... or anything else dependant on this data.
        let clone = ObjectUtils.Clone(data);

        //remove all _tempId entries on the data.
        clone = this._RemoveTempId(clone);

        //return cleaned copy.
        return clone;
    }

    _RemoveTempId(data) {
        //only work on objects. If anything else lets skip this logic and just return what we're passed.
        if(data instanceof Object) {
            //drop _tempId if it exists on this object.
            if(data._tempId) {
                delete data._tempId;
            }

            //for each property on the object.
            Object.keys(data).forEach((x) => {
                if(data[x] instanceof Object) {
                    //if this property is an object run this logic recursively.
                    data[x] = this._RemoveTempId(data[x]);
                } else if (Array.isArray(data[x])) {
                    //if this is an array run this logic for each entry.
                    for(let i = 0; i < data[x].length; i++) {
                        data[x][i] = this._RemoveTempId(data[x][i]);
                    }
                }
            });
        }

        return data;
    }

    // default implementations
    // which can be overridden at the
    // service level -- I feel like
    // these should come from the model :/
    //
    GetTitle(model, defaultTitle = "") {
        return model != null ? model[this.TitleProperty] : defaultTitle;
    }

    GetSubtitle(model) {
        return null;
    }
    
    GetIcon(model) {
        return null;
    }

    /**
     * Generates a string containing the values of all
     * searchable properties of the model, concatenated
     * together with spaces as the delimiter.
     * 
     * @param model
     * @returns {string}
     * @constructor
     */
    GetSearchableString(model) {
        return this.SearchProperties.map((prop) => {
            if (model && model[prop]) {
                return model[prop].toString().toLowerCase().trim();
            }
            return '';
        }).join(' ');
    }
    
    GetPrimaryKey(model) {
        return model?.[this.PrimaryKeyProperty] ?? null;
    }

    @autobind
    SortModels(a, b, value) {
        const titleA = this.GetTitle(a);
        const titleB = this.GetTitle(b);
        let result = 0;

        // primary sort
        if (titleA && titleB == null) {
            result =  -1;
        } else if (titleA == null && titleB) {
            result = 1;
        } else {
            result = titleA.localeCompare(titleB);
        }

        // secondary sort
        if (result === 0) {
            result = (this.GetPrimaryKey(a) < this.GetPrimaryKey(b) ? 1 : -1);
        }

        return result;
    }
}
